import { debugLog } from "./debug";
import {
  SSML_CONFIG,
  AZURE_AVATAR_CONFIG,
  AVATAR_STATUS_CONFIG,
} from "../constants/avatar.constants";

/**
 * Escape XML characters for SSML
 */
export const escapeXml = (text: string): string => {
  return text.replace(/[&<>"']/g, (match) => {
    switch (match) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      case "'":
        return "&#39;";
      default:
        return match;
    }
  });
};

/**
 * Generate SSML for avatar speech with emotion support
 */
export const generateSSML = (
  text: string,
  voiceName: string,
  emotion?: string,
  customRate?: number
): string => {
  const escapedText = escapeXml(text);
  const volume = AZURE_AVATAR_CONFIG.SSML_VOLUME;
  const rate = customRate !== undefined ? customRate.toString() : AZURE_AVATAR_CONFIG.SSML_RATE;
  const pitch = AZURE_AVATAR_CONFIG.SSML_PITCH;

  if (emotion) {
    return `<speak version='${SSML_CONFIG.VERSION}' xmlns='${SSML_CONFIG.XMLNS}' xmlns:mstts='${SSML_CONFIG.XMLNS_MSTTS}' xml:lang='${SSML_CONFIG.LANG}'>
        <voice name='${voiceName}'>
          <mstts:express-as style='${emotion}'>
            <prosody volume='${volume}' rate='${rate}' pitch='${pitch}'>${escapedText}</prosody>
          </mstts:express-as>
        </voice>
      </speak>`;
  }

  return `<speak version='${SSML_CONFIG.VERSION}' xmlns='${SSML_CONFIG.XMLNS}' xml:lang='${SSML_CONFIG.LANG}'>
        <voice name='${voiceName}'>
          <prosody volume='${volume}' rate='${rate}' pitch='${pitch}'>${escapedText}</prosody>
        </voice>
      </speak>`;
};

/**
 * Create status overlay HTML
 */
export const createStatusOverlayHTML = (
  message: string,
  type: "loading" | "ready" | "error"
): string => {
  const color =
    AVATAR_STATUS_CONFIG.COLORS[
      type.toUpperCase() as keyof typeof AVATAR_STATUS_CONFIG.COLORS
    ];
  const zIndex = AVATAR_STATUS_CONFIG.OVERLAY_Z_INDEX;

  return `
    <div style="
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: rgba(0, 0, 0, 0.7);
      z-index: ${zIndex};
    ">
      <div style="
        color: var(--color-white, #FFF);
        text-align: center;
        font-family: Interstate;
        font-size: 24px;
        font-style: normal;
        font-weight: 500;
        line-height: 20px;
        letter-spacing: 16px;
        text-transform: uppercase;
      ">${message}</div>
    </div>
  `;
};

/**
 * Configure video element for avatar display
 */
export const configureVideoElement = (videoElement: HTMLVideoElement): void => {
  videoElement.autoplay = true;
  videoElement.playsInline = true;
  videoElement.muted = false;
  videoElement.classList.remove("hidden");
  videoElement.style.display = "block";
  videoElement.style.width = "100%";
  videoElement.style.height = "100%";
  videoElement.style.objectFit = "cover";
  videoElement.style.objectPosition = "center";
  videoElement.style.maxWidth = "100%";
  videoElement.style.maxHeight = "100%";
  videoElement.style.transformOrigin = "center";
};

/**
 * Get Azure Avatar configuration from backend API
 * Fetches configuration securely from the server instead of environment variables
 */
export const getAzureAvatarConfigFromEnv = async () => {
  // Always fetch from backend for security
  try {
    const backendUrl =
      process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_BACKEND_URL;

    if (!backendUrl) {
      throw new Error(
        "Backend URL not configured. Please set REACT_APP_SERVER_URL in .env file."
      );
    }

    // Remove /api suffix if present to avoid double /api
    const baseUrl = backendUrl.replace(/\/api\/?$/, "");
    const response = await fetch(`${baseUrl}/api/avatarconfig`);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch avatar configuration: ${response.status} ${response.statusText}`
      );
    }

    const config = await response.json();

    debugLog(`🔍 [DEBUG] /api/avatarconfig raw response:`, JSON.stringify(config));
    debugLog(`🔍 [DEBUG] config.endpoint value: "${config.endpoint}", type: ${typeof config.endpoint}`);

    if (!config.subscriptionKey) {
      throw new Error("Avatar subscription key not provided by backend");
    }

    if (!config.region) {
      throw new Error("Avatar region not provided by backend");
    }

    if (!config.language) {
      throw new Error("Avatar language not provided by backend");
    }

    if (!config.voiceName) {
      throw new Error("Avatar voice name not provided by backend");
    }

    if (!config.avatarCharacter) {
      throw new Error("Avatar character not provided by backend");
    }

    const result = {
      subscriptionKey: config.subscriptionKey,
      serviceRegion: config.region,
      language: config.language,
      voiceName: config.voiceName,
      avatarCharacter: config.avatarCharacter,
      avatarStyle: config.avatarStyle || "",
      customized: config.customized ?? true,
      useBuiltInVoice: config.useBuiltInVoice ?? true,
      endpoint: config.endpoint,
      deploymentId: config.deploymentId,
    };

    debugLog(`🔍 [DEBUG] getAzureAvatarConfigFromEnv returning:`, JSON.stringify({ ...result, subscriptionKey: '[REDACTED]' }));

    return result;
  } catch (error) {
    throw new Error(
      `Failed to load avatar configuration from backend: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
};

/**
 * Unmute all audio elements on the page
 */
export const unmuteAllAudioElements = (): void => {
  const audioElements = document.querySelectorAll("audio");
  audioElements.forEach((audio) => {
    audio.muted = false;
  });
};
